<template>
    <div>
        <card-header title="New Enrollment" icon="fa-plus"/>

        <card-body>
            <card-list>
                <subheader-list-item icon="fa-align-left" title="Enrollment"/>
                <container-list-item padded no-border>
                    <div class="columns">
                        <div class="column">
                            <b-field label="Course" label-position="inside">
                                <b-select expanded v-model="courseSelect" @input="changeCourse" :loading="loading" placeholder="Select a course">
                                    <option :value="c" v-for="c in courses" :key="c.id">{{ c.title }}</option>
                                </b-select>
                            </b-field>
                        </div>
                    </div>
                    <div class="columns">
                        <div class="column">
                            <b-field label="Configuration" label-position="inside">
                                <b-select v-if="courseSelect" expanded v-model="configSelect" placeholder="Select clock hours">
                                    <option :value="cfg" v-for="cfg in courseSelect.course_config" :key="cfg.id">{{ cfg.units }} Clock Hours<template v-if="cfg.title"> &mdash; {{ cfg.title }}</template></option>
                                </b-select>
                                <b-select v-else disabled expanded></b-select>
                            </b-field>
                        </div>
                    </div>
                    <div class="columns">
                        <div class="column">
                            <b-field label="Custom Name (optional)" label-position="inside">
                                <b-input v-model="customName"></b-input>
                            </b-field>
                        </div>
                        <div class="column is-3" v-if="suffix1Options.length">
                            <b-field label="Suffix" label-position="inside">
                                <b-select expanded v-model="custom1">
                                    <option :value="''">&mdash;</option>
                                    <option :value="o" v-for="o in suffix1Options" :key="o">{{ o }}</option>
                                </b-select>
                            </b-field>
                        </div>
                        <div class="column is-3" v-if="suffix2Options.length">
                            <b-field label="Suffix" label-position="inside">
                                <b-select expanded v-model="custom2">
                                    <option :value="''">&mdash;</option>
                                    <option :value="o" v-for="o in suffix2Options" :key="o">{{ o }}</option>
                                </b-select>
                            </b-field>
                        </div>
                    </div>
                    <div class="columns">
                        <div class="column is-6">
                            <b-field label="Payment Plan (optional)" label-position="inside">
                                <b-select expanded v-model="planSelect" @input="changePlan" :disabled="!plans.length" :placeholder="plans.length ? 'Select a payment plan' : 'No payment plans'">
                                    <option :value="null">None</option>
                                    <option :value="p" v-for="p in plans" :key="p.id">{{ planLabel(p) }}</option>
                                </b-select>
                            </b-field>
                        </div>
                        <div class="column is-6">
                            <b-field label="Expire Date (optional)" label-position="inside">
                                <b-datepicker position="is-bottom-left" append-to-body v-model="expiresOn" expanded></b-datepicker>
                                <p class="control">
                                    <b-button type="is-warning" outlined @click="expiresOn = null"><i class="fas fa-times ml-1 mr-1"></i></b-button>
                                </p>
                            </b-field>
                        </div>
                    </div>
                    <p class="help" v-if="planSelect && !planSelect.active"><i class="fas fa-lock mr-1"></i>This plan is not yet active &mdash; the enrollment stays locked until the payment agreement completes.</p>
                    <p class="help">The student completes the study-plan setup before this course activates.</p>
                </container-list-item>
            </card-list>
        </card-body>

        <card-footer class="pt-3 pb-3">
            <div class="columns">
                <div class="column has-text-right">
                    <b-button
                        type="is-primary"
                        size="is-small"
                        @click="submit"
                        :disabled="!configSelect || submitting"
                    ><i class="fas fa-plus mr-2"></i>New Enrollment</b-button>
                </div>
            </div>
        </card-footer>
    </div>
</template>

<script>
    import CardHeader from "@/TIER/components/CardHeader";
    import CardBody from "@/TIER/components/CardBody";
    import CardFooter from "@/TIER/components/CardFooter";
    import CardList from "@/TIER/components/CardList";
    import SubheaderListItem from "@/TIER/components/SubheaderListItem";
    import ContainerListItem from "@/TIER/components/ContainerListItem";

    import {client as http} from '../../http_client';
    import moment from 'moment';

    export default {
        components: {CardHeader, CardBody, CardFooter, CardList, SubheaderListItem, ContainerListItem},
        props: ['card', 'props'],
        data() {
            return {
                courses: [],
                plans: [],
                courseSelect: null,
                configSelect: null,
                planSelect: null,
                customName: '',
                custom1: '',
                custom2: '',
                expiresOn: null,
                loading: true,
                submitting: false
            };
        },
        computed: {
            suffix1Options: function() {
                const n = this.courseSelect && this.courseSelect.custom_naming;
                return (n && Array.isArray(n.custom1_options)) ? n.custom1_options : [];
            },
            suffix2Options: function() {
                const n = this.courseSelect && this.courseSelect.custom_naming;
                return (n && Array.isArray(n.custom2_options)) ? n.custom2_options : [];
            }
        },
        methods: {
            changeCourse: function() {
                this.configSelect = null;
                this.custom1 = '';
                this.custom2 = '';
                // Default the custom name to the course's naming prefix, else the title
                const naming = this.courseSelect ? this.courseSelect.custom_naming : null;
                this.customName = (naming && naming.prefix) || (this.courseSelect ? this.courseSelect.title : '');
            },
            planLabel: function(p) {
                const name = p.nickname || ('Plan #' + p.id);
                const total = (p.total !== undefined && p.total !== null) ? ' — $' + Number(p.total).toFixed(2) : '';
                const status = p.status ? ' (' + p.status + ')' : (p.active ? '' : ' (inactive)');
                return name + total + status;
            },
            changePlan: function() {
                // Expiration is typically derived from the plan's agreement
                const p = this.planSelect;
                if (!p) return;
                let af = p.agreement_fields;
                if (typeof af === 'string') {
                    try { af = JSON.parse(af); } catch (e) { af = null; }
                }
                let exp = (af && af.expiration_date) ? moment(af.expiration_date, 'YYYY-MM-DD') : null;
                if ((!exp || !exp.isValid()) && p.created_at) exp = moment(p.created_at).add(3, 'years');
                if (exp && exp.isValid()) this.expiresOn = exp.toDate();
            },
            submit: function() {
                if (!this.configSelect) return;

                // Build the display name like legacy/quote: base + suffixes
                let parts = [(this.customName || '').trim()];
                if (this.custom1 && this.custom1 !== 'N/A') parts.push(this.custom1.trim());
                if (this.custom2 && this.custom2 !== 'N/A') parts.push(this.custom2.trim());
                const customCourseName = parts.filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();

                let payload = {
                    user_id: parseInt(this.props.userId),
                    course_config_id: this.configSelect.id,
                    custom_course_name: customCourseName,
                    plan_id: this.planSelect ? this.planSelect.id : null,
                    expires_on: this.expiresOn ? moment(this.expiresOn).format('YYYY-MM-DD') : null
                };

                this.submitting = true;
                this.$emit('loading', true);
                http.post('enrollments', payload, {force: true}).then(response => {
                    this.$reloadCard('course-enrollments');
                    this.$reloadCard('user-details');
                    this.$closeCard(this.card);
                }).catch(err => {
                    this.submitting = false;
                    this.$emit('loading', false);
                });
            }
        },
        mounted() {
            this.$emit('loading', true);
            Promise.all([
                http.get('enrollments/enrollable', {force: true}),
                http.get('users/' + this.props.userId, {force: true})
            ]).then(([coursesRes, userRes]) => {
                this.courses = coursesRes.data || [];
                this.plans = (userRes.data && userRes.data.payment_plans) ? userRes.data.payment_plans : [];
                this.loading = false;
                this.$emit('loading', false);
            }).catch(err => {
                this.loading = false;
                this.$emit('loading', false);
            });
        }
    };
</script>
